import React from "react";
import { Box, Typography } from "@mui/material";
import { UserRoundSearch, Target, GraduationCap, ClipboardCheck, LayoutDashboard, BarChart3, MapPin } from "lucide-react";
import SectionHeading from "../../components/ui/SectionHeading";

const programs = [
  { title: "Recruitment support", copy: "Shortlist trained candidates and coordinate hiring pipelines.", icon: UserRoundSearch, tag: "Sourcing" },
  { title: "Talent acquisition", copy: "Build role-based talent programs for cloud, security, networking, DevOps, and AI.", icon: Target, tag: "Strategy" },
  { title: "Workforce development", copy: "Upskill existing teams and prepare future-ready internal talent.", icon: GraduationCap, tag: "Enablement" },
  { title: "Candidate screening", copy: "Assess technical ability, communication, projects, and certification readiness.", icon: ClipboardCheck, tag: "Assessment" },
  { title: "Hiring dashboard", copy: "Track requests, candidate progress, interviews, and placement outcomes.", icon: LayoutDashboard, tag: "Visibility" },
  { title: "Placement analytics", copy: "Measure conversion, source quality, role demand, and hiring velocity.", icon: BarChart3, tag: "Insights" },
];

export default function HiringPrograms() {
  return (
    <Box component="section" sx={{ position: "relative", bgcolor: "#FAFBFF", py: { xs: 8, md: 11 }, overflow: "hidden" }}>
      {/* Blueprint grid backdrop */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(29,78,216,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(29,78,216,0.045) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage: "radial-gradient(ellipse 70% 60% at 50% 0%, black 0%, transparent 78%)",
          WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 0%, black 0%, transparent 78%)",
        }}
      />

      <div className="section-shell" style={{ position: "relative" }}>
        <SectionHeading
          eyebrow="Hiring programs"
          title="Connect enterprise demand with trained candidate supply."
          copy="Smart lead qualification captures hiring needs, role requirements, timelines, and screening criteria so recruitment partners receive qualified workflows."
        />

        {/* Route map */}
        <Box sx={{ position: "relative", mt: { xs: 7, md: 9 } }}>
          {/* Central route line (desktop) */}
          <Box
            aria-hidden
            sx={{
              display: { xs: "none", md: "block" },
              position: "absolute",
              left: "50%",
              top: 28,
              bottom: 28,
              width: "2px",
              transform: "translateX(-50%)",
              background:
                "repeating-linear-gradient(to bottom, rgba(37,99,235,0.35) 0px, rgba(37,99,235,0.35) 8px, transparent 8px, transparent 16px)",
            }}
          />
          {/* Side route line (mobile/tablet) */}
          <Box
            aria-hidden
            sx={{
              display: { xs: "block", md: "none" },
              position: "absolute",
              left: 23,
              top: 28,
              bottom: 28,
              width: "2px",
              background:
                "repeating-linear-gradient(to bottom, rgba(37,99,235,0.35) 0px, rgba(37,99,235,0.35) 8px, transparent 8px, transparent 16px)",
            }}
          />

          {programs.map(({ title, copy, icon: Icon, tag }, i) => {
            const isLeft = i % 2 === 0;
            const isFirst = i === 0;
            const isLast = i === programs.length - 1;

            return (
              <Box
                key={title}
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: { xs: "flex-start", md: "center" },
                  justifyContent: { md: isLeft ? "flex-end" : "flex-start" },
                  mb: { xs: 4.5, md: i === programs.length - 1 ? 0 : 5 },
                  pl: { xs: 7.5, md: 0 },
                }}
              >
                {/* Station marker on the route */}
                <Box
                  aria-hidden
                  sx={{
                    position: "absolute",
                    left: { xs: 23, md: "50%" },
                    top: { xs: 4, md: "50%" },
                    transform: { xs: "translate(-50%, 0)", md: "translate(-50%, -50%)" },
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    bgcolor: "#2563EB",
                    boxShadow: "0 0 0 5px rgba(37,99,235,0.14), 0 0 0 1px rgba(37,99,235,0.4)",
                    zIndex: 2,
                  }}
                />

                {/* Connector stub from center line to the card (desktop only) */}
                <Box
                  aria-hidden
                  sx={{
                    display: { xs: "none", md: "block" },
                    position: "absolute",
                    top: "50%",
                    transform: "translateY(-50%)",
                    left: isLeft ? "auto" : "50%",
                    right: isLeft ? "50%" : "auto",
                    width: 40,
                    height: "2px",
                    bgcolor: "rgba(37,99,235,0.3)",
                  }}
                />

                <Box
                  role="group"
                  sx={{
                    position: "relative",
                    width: { xs: "100%", md: "calc(50% - 64px)" },
                    bgcolor: "white",
                    border: "1px solid rgba(15,23,42,0.08)",
                    borderRadius: "16px",
                    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                    p: 2.75,
                    overflow: "hidden",
                    transition: "transform 0.28s cubic-bezier(.2,.8,.2,1), box-shadow 0.28s ease, border-color 0.28s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 22px 44px -22px rgba(37,99,235,0.3)",
                      borderColor: "rgba(37,99,235,0.28)",
                    },
                    "&:hover .hp-icon": {
                      background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
                      color: "white",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
                    <Box
                      className="hp-icon"
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "12px",
                        bgcolor: "rgba(37,99,235,0.08)",
                        color: "#2563EB",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        transition: "all 0.25s ease",
                      }}
                    >
                      <Icon size={20} strokeWidth={2.1} />
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 10.5,
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: "#2563EB",
                          mb: 0.3,
                        }}
                      >
                        {tag}
                      </Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: "1.02rem", color: "#0F172A", letterSpacing: "-0.01em", lineHeight: 1.25 }}>
                        {title}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="body2" color="text.secondary" lineHeight={1.6} sx={{ fontSize: 13.5, mt: 1.5 }}>
                    {copy}
                  </Typography>
                </Box>
              </Box>
            );
          })}

          {/* Start / end markers on the route */}
          <Box
            aria-hidden
            sx={{
              display: { xs: "none", md: "flex" },
              position: "absolute",
              left: "50%",
              top: -8,
              transform: "translate(-50%, -100%)",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            <MapPin size={14} color="#2563EB" strokeWidth={2.3} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#2563EB", letterSpacing: "0.04em", textTransform: "uppercase" }}>
              Start
            </Typography>
          </Box>
        </Box>
      </div>
    </Box>
  );
}